import React, { useEffect, useMemo, useRef, useState } from "react";
import commentary from "../../assets/images/commentary.png";
import casino from "../../assets/images/menu-99998.png";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Modal } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { toast } from "react-toastify";
import {
  fetchCasinoConversionRate,
  fetchQtechGames,
} from "../../store/slices/casinoSlice";
import casinoPoint100 from "../../assets/images/casinopoint100.png";
import {
  customGamesData,
  liveCasinoProvider,
  customAura,
} from "./Qtechconstant";

const SUBCATEGORY_ICON_BASE = "https://speedcdn.io/assets/int_tab_icons";

const normalizeValue = (value) => (value || "").toString().trim().toLowerCase();

const slugify = (str) =>
  normalizeValue(str)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

function getCategoryDisplayLabel(category) {
  if (!category || typeof category !== "string") return category;
  const trimmed = category.trim();
  const lastSlash = trimmed.lastIndexOf("/");
  return lastSlash >= 0 ? trimmed.slice(lastSlash + 1) : trimmed;
}

const CasinoQTList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const routeCategory = searchParams.get("category");

  const { id, subTab } = useParams();

  const filterTabRefs = useRef([]);
  const categoryStripRefs = useRef([]);

  const activeFilterIndex = useMemo(() => {
    const normalizedCategory = (routeCategory || "").toUpperCase().trim();
    if (!normalizedCategory) return 0;
    const index = liveCasinoProvider.findIndex(
      (tab) => (tab.name || "").toUpperCase().trim() === normalizedCategory,
    );
    return index >= 0 ? index : 0;
  }, [routeCategory]);

  const loading = useAppSelector((state) => state.casino?.loading || false);
  const casinoConversionRate = useAppSelector(
    (state) => state.casino?.casinoConversionRate,
  );

  const auth = useAppSelector((state) => state.auth);
  const [dismissedConversionRateDialog, setDismissedConversionRateDialog] =
    useState(false);

  useEffect(() => {
    dispatch(fetchCasinoConversionRate());
  }, [dispatch]);

  const conversionRateValue =
    casinoConversionRate?.data?.casino_conversion_rate;
  const shouldShowConversionRateDialog = conversionRateValue === 10;

  const [active, setActive] = useState("All");

  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState("ALL");

  useEffect(() => {
    if (id) {
      setActive(id);
    }
    if (subTab) {
      setSelectedTab(subTab);
    }
  }, [id, subTab]);

  useEffect(() => {
    dispatch(fetchQtechGames({ providerCode: active }));
  }, [active, dispatch]);

  const liveCasino = useAppSelector(
    (state) => state?.casino?.qtechGamesByProvider?.[active] || [],
  );

  useEffect(() => {
    if (liveCasino?.length > 0) {
      let categoryArr = [];

      if (active === "NetEnt") {
        categoryArr = ["ALL", "NEW SLOTS"];
      } else {
        const categories = new Set();

        liveCasino.forEach((item) => {
          if (item?.category) {
            categories.add(item.category);
          }
        });

        categoryArr = ["ALL", ...Array.from(categories)];
      }

      setTabs(categoryArr);
      setSelectedTab(subTab || categoryArr[0] || "ALL");
    } else {
      setTabs([]);
      setSelectedTab(subTab || "ALL");
    }
  }, [liveCasino, active, subTab]);

  const handleMainTab = (index, tab) => {
    setActive(tab?.id);
    filterTabRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  const handleGameClick = (game) => {
    if (!auth?.token) {
      if (
        typeof window !== "undefined" &&
        typeof window.openLoginDialog === "function"
      ) {
        window.openLoginDialog();
      } else {
        toast.error("Please login to continue.", {
          toastId: "casino_launch_login_required",
        });
      }
      return;
    }

    if (auth?.user?.is_demo || auth?.isDemoLogin) {
      toast.error("Demo users are not allowed to play casino games.", {
        toastId: "demo_casino_restricted",
      });
      return;
    }
    if (game?.category) {
      navigate(`/iframe/${game?.gameCode}/${game?.gameId}/${game?.category}`);
    } else {
      navigate(`/iframe/${game?.gameCode}/${game?.gameId}`);
    }
  };

  const formatCategoryName = (category) => {
    return (
      category
        ?.split("/")
        .pop()
        ?.replace(/_/g, " ")
        ?.toLowerCase()
        ?.split(" ")
        ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        ?.join(" ") || ""
    );
  };

  const uniqueTabs = Array.from(
    new Map(
      tabs.map((tab) => [
        formatCategoryName(tab),
        {
          value: tab,
          label: formatCategoryName(tab),
        },
      ]),
    ).values(),
  );

  const [games, setGames] = useState([]);

  useEffect(() => {
    if (active === "All") {
      const requiredData = customGamesData?.[selectedTab]?.map((item) => ({
        urlThumb: item?.images?.find((img) => img?.type === "banner")?.url,
        gameName: item?.name,
        gameId: item?.id,
        gameCode: item?.provider?.id,
      }));

      setGames(requiredData || []);
      return;
    }

    if (liveCasino?.length > 0 && active && selectedTab) {
      let requiredData = [];

      if (selectedTab === "ALL") {
        requiredData = liveCasino.map((item) => ({
          urlThumb: item?.images?.[1]?.url,
          gameName: item?.name,
          gameId: item?.id,
          gameCode: item?.provider?.id,
        }));
      } else {
        requiredData = liveCasino
          .filter((item) => item?.category === selectedTab)
          .map((item) => ({
            urlThumb: item?.images?.[1]?.url,
            gameName: item?.name,
            gameId: item?.id,
            gameCode: item?.provider?.id,
          }));
      }

      const timer = setTimeout(() => {
        setGames(requiredData);
      }, 200);

      return () => clearTimeout(timer);
    }

    setGames([]);
  }, [liveCasino, active, selectedTab]);

  const groupedAuraGames = customAura.reduce((acc, item) => {
    const { url_thumb, game_name, game_id, provider_name, category } = item;

    const game = {
      urlThumb: url_thumb,
      gameName: game_name,
      gameId: game_id,
      gameCode: provider_name,
      category,
      isAura: true,
    };

    // All games
    if (!acc.All) {
      acc.All = [];
    }
    acc.All.push(game);

    // Group by category
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(game);

    return acc;
  }, {});

  return (
    <section className="section listing_page">
      <Modal
        show={shouldShowConversionRateDialog && !dismissedConversionRateDialog}
        onHide={() => setDismissedConversionRateDialog(true)}
        centered
        className="engcasinomodalbgimg_main"
      >
        <div className="engcasinomodalbgimg">
          <button
            type="button"
            onClick={() => setDismissedConversionRateDialog(true)}
            className="close"
            aria-label="Close"
          >
            ×
          </button>

          <img alt="" className="img-fluid" src={casinoPoint100} />
          <h2 className="lobby">1 POINT = {conversionRateValue}</h2>
          <p className="maximum_bet">2,00,000</p>
          <button
            type="button"
            className="virtual-close-btn"
            onClick={() => {
              setDismissedConversionRateDialog(true);
            }}
          >
            &nbsp;
          </button>
        </div>
      </Modal>
      <div className="row">
        <div className="col-12 d-flex">
          <div className="commentary">
            <img src={commentary} alt="commentary" />
            <div className="commentary-ticker" aria-live="polite">
              <span>
                🏆𝐈𝐂𝐂 𝐌𝐄𝐍'𝐒 𝐓𝟐𝟎 𝐖𝐎𝐑𝐋𝐃 𝐂𝐔𝐏 𝟐𝟎𝟐𝟔🏆 𝐂𝐔𝐏 𝐖𝐈𝐍𝐍𝐄𝐑 𝐁𝐄𝐓𝐒 𝐒𝐓𝐀𝐑𝐓𝐄𝐃 ⚽𝐔𝐄𝐅𝐀
                𝐂𝐇𝐀𝐌𝐏𝐈𝐎𝐍𝐒 𝐋𝐄𝐀𝐆𝐔𝐄 🏆 𝐂𝐔𝐏 𝐖𝐈𝐍𝐍𝐄𝐑 𝐁𝐄𝐓𝐒 𝐒𝐓𝐀𝐑𝐓𝐄𝐃 𝐈𝐍 𝐎𝐔𝐑 𝐄𝐗𝐂𝐇𝐀𝐍𝐆𝐄 ⚽
                🏏𝐏𝐋𝐀𝐘 𝐓𝐇𝐄 𝐆𝐄𝐍𝐈𝐄 𝐂𝐎𝐌𝐁𝐎 𝐒𝐏𝐄𝐂𝐈𝐀𝐋 𝐁𝐄𝐓 𝐈𝐍 𝐎𝐔𝐑 𝐄𝐗𝐂𝐇𝐀𝐍𝐆𝐄 𝐀𝐍𝐃 𝐌𝐔𝐋𝐓𝐈𝐏𝐋𝐘
                �𝐎𝐔𝐑 �𝐖𝐈𝐍𝐍𝐈𝐍𝐆𝐒 ,𝐎𝐔𝐑 𝐄𝐗𝐂𝐋𝐔𝐒𝐈𝐕𝐄 𝐏𝐑𝐄𝐌𝐈𝐔𝐌 𝐌𝐀𝐑𝐊𝐄𝐓 𝐅𝐎𝐑 (𝐒𝐑𝐋) 𝐈𝐒 𝐍𝐎𝐖
                𝐓𝐀𝐑𝐓𝐄𝐃 🏏 𝐃𝐑𝐄𝐀𝐌 𝐁𝐈𝐆 𝐖𝐈𝐍 𝐁𝐈𝐆 🏆
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="col-lg-12 listing_page_12">
        <div className="middle-page-content home-page">
          <div className="game-slider mb-2">
            <div className="coupon-card">
              <div className="card">
                <div className="card-body card-content p-0">
                  <div className="bet-table-header">
                    <div className="row align-items-center g-0">
                      <div className="col-md-4">
                        <span className="list-sport-title">
                          <img
                            className="img-fluid game-icon-img"
                            src={casino}
                            alt="casino"
                          />
                          Casino
                        </span>
                      </div>
                      <div className="casino_searchbar">
                        <div className="input-group">
                          <input
                            type="text"
                            placeholder="Search Games..."
                            className="ng-untouched ng-pristine ng-valid"
                            aria-label="Search Games"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="bi bi-search"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bet-table-body">
                    <div className="home-products-container mt-1">
                      <div className="row row5">
                        <div className="col-md-12">
                          <div className="casino_tabs_ul tab-container">
                            <ul
                              role="tablist"
                              className="nav nav-tab fancy-subtabs"
                              aria-label="Tabs"
                            >
                              {liveCasinoProvider?.map((tab, index) => (
                                <li
                                  key={tab?.tabName}
                                  ref={(el) =>
                                    (filterTabRefs.current[index] = el)
                                  }
                                  className={`nav-item${active === tab?.id ? " active" : ""}`}
                                >
                                  <button
                                    type="button"
                                    role="tab"
                                    className={`nav-link${active === tab?.id ? " active" : ""}`}
                                    aria-controls={`parentTab-${index}`}
                                    aria-selected={active === tab?.id}
                                    id={`parentTab-${index}-link`}
                                    tabIndex={0}
                                    onClick={() => handleMainTab(index, tab)}
                                  >
                                    <span />
                                    <span> {tab?.name} </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                            <div className="tab-content">
                              <div
                                id={`parentTab-${activeFilterIndex}`}
                                className="tab-pane active"
                              >
                                <div className="casino_tabs_ul tab-container">
                                  <ul className="nav nav-tab new_icasino_tabs mt-2">
                                    {active === "All"
                                      ? Object.keys(customGamesData)?.map(
                                          (key) => {
                                            const isActive =
                                              key === selectedTab;
                                            return (
                                              <li
                                                key={key}
                                                ref={(el) =>
                                                  (categoryStripRefs.current[
                                                    key
                                                  ] = el)
                                                }
                                                className={`nav-item${isActive ? " active" : ""}`}
                                              >
                                                <Link
                                                  to="#"
                                                  role="tab"
                                                  tabIndex={0}
                                                  className={`nav-link${isActive ? " active" : ""}`}
                                                  aria-controls={`tab-${key}`}
                                                  aria-selected={isActive}
                                                  id={`childTab-${activeFilterIndex}-${key}`}
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    setSelectedTab(key);
                                                    categoryStripRefs.current[
                                                      key
                                                    ]?.scrollIntoView({
                                                      behavior: "smooth",
                                                      block: "nearest",
                                                      inline: "center",
                                                    });
                                                  }}
                                                >
                                                  <span />
                                                  <span>
                                                    <img
                                                      className="img-fluid"
                                                      src={`${SUBCATEGORY_ICON_BASE}/${slugify(
                                                        getCategoryDisplayLabel(
                                                          key,
                                                        ),
                                                      )}.png`}
                                                      alt=""
                                                      onError={(e) => {
                                                        e.currentTarget.onerror =
                                                          null;
                                                        e.currentTarget.src = `${SUBCATEGORY_ICON_BASE}/others.png`;
                                                      }}
                                                    />{" "}
                                                    {key}
                                                  </span>
                                                </Link>
                                              </li>
                                            );
                                          },
                                        )
                                      : active === "Aura"
                                        ? Object.keys(groupedAuraGames)?.map(
                                            (key) => {
                                              const isActive =
                                                key === selectedTab;
                                              return (
                                                <li
                                                  key={key}
                                                  ref={(el) =>
                                                    (categoryStripRefs.current[
                                                      key
                                                    ] = el)
                                                  }
                                                  className={`nav-item${isActive ? " active" : ""}`}
                                                >
                                                  <Link
                                                    to="#"
                                                    role="tab"
                                                    tabIndex={0}
                                                    className={`nav-link${isActive ? " active" : ""}`}
                                                    aria-controls={`tab-${key}`}
                                                    aria-selected={isActive}
                                                    id={`childTab-${activeFilterIndex}-${key}`}
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      setSelectedTab(key);
                                                      categoryStripRefs.current[
                                                        key
                                                      ]?.scrollIntoView({
                                                        behavior: "smooth",
                                                        block: "nearest",
                                                        inline: "center",
                                                      });
                                                    }}
                                                  >
                                                    <span />
                                                    <span>
                                                      <img
                                                        className="img-fluid"
                                                        src={`${SUBCATEGORY_ICON_BASE}/${slugify(
                                                          getCategoryDisplayLabel(
                                                            key,
                                                          ),
                                                        )}.png`}
                                                        alt=""
                                                        onError={(e) => {
                                                          e.currentTarget.onerror =
                                                            null;
                                                          e.currentTarget.src = `${SUBCATEGORY_ICON_BASE}/others.png`;
                                                        }}
                                                      />{" "}
                                                      {key}
                                                    </span>
                                                  </Link>
                                                </li>
                                              );
                                            },
                                          )
                                        : uniqueTabs?.map((tab) => {
                                            const { value, label } = tab;
                                            const isActive =
                                              value === selectedTab;
                                            return (
                                              <li
                                                key={value}
                                                ref={(el) =>
                                                  (categoryStripRefs.current[
                                                    value
                                                  ] = el)
                                                }
                                                className={`nav-item${isActive ? " active" : ""}`}
                                              >
                                                <Link
                                                  to="#"
                                                  role="tab"
                                                  tabIndex={0}
                                                  className={`nav-link${isActive ? " active" : ""}`}
                                                  aria-controls={`tab-${value}`}
                                                  aria-selected={isActive}
                                                  id={`childTab-${activeFilterIndex}-${value}`}
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    setSelectedTab(value);
                                                    categoryStripRefs.current[
                                                      value
                                                    ]?.scrollIntoView({
                                                      behavior: "smooth",
                                                      block: "nearest",
                                                      inline: "center",
                                                    });
                                                  }}
                                                >
                                                  <span />
                                                  <span>
                                                    <img
                                                      className="img-fluid"
                                                      src={`${SUBCATEGORY_ICON_BASE}/${slugify(
                                                        getCategoryDisplayLabel(
                                                          label,
                                                        ),
                                                      )}.png`}
                                                      alt=""
                                                      onError={(e) => {
                                                        e.currentTarget.onerror =
                                                          null;
                                                        e.currentTarget.src = `${SUBCATEGORY_ICON_BASE}/others.png`;
                                                      }}
                                                    />{" "}
                                                    {label}
                                                  </span>
                                                </Link>
                                              </li>
                                            );
                                          })}
                                  </ul>
                                  <div className="tab-content">
                                    <div className="csn_div">
                                      {loading && (
                                        <div className="w-100 text-center py-3">
                                          Loading games...
                                        </div>
                                      )}
                                      {(active === "Aura"
                                        ? groupedAuraGames?.[selectedTab] ||
                                          groupedAuraGames?.All
                                        : games
                                      ).map((game) => (
                                        <div
                                          key={
                                            game.gameId ||
                                            game.gameCode ||
                                            game.gameName
                                          }
                                          className="csn_thumb"
                                        >
                                          <img
                                            className="img-fluid ng-lazyloaded"
                                            src={game.urlThumb || ""}
                                            alt={game.gameName}
                                            onClick={() =>
                                              handleGameClick(game)
                                            }
                                          />
                                          <div className="casino-name">
                                            {game.gameName}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CasinoQTList;
